// src/components/Systemnotification/Systemnotification.jsx
import React, { useEffect, useState, useCallback } from 'react';
import Menu from '../Menu/Menu';
import api from '../../utils/axios.secure';
import './css/systemnotification.css';

// ISO 문자열 → "YYYY-MM-DD HH:mm:ss" (KST)
const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const yyyy = kst.getFullYear();
  const mm = String(kst.getMonth() + 1).padStart(2, '0');
  const dd = String(kst.getDate()).padStart(2, '0');
  const hh = String(kst.getHours()).padStart(2, '0');
  const mi = String(kst.getMinutes()).padStart(2, '0');
  const ss = String(kst.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

const CHANNELS = ['APP', 'WEB', 'BOTH'];

const ManagerSystemNotification = () => {
  const [channel, setChannel] = useState('APP');
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // 편집 모달 상태
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null); // row 전체
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');

  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async (ch) => {
    const currentChannel = ch || channel;
    setLoading(true);
    setErrorMsg('');

    try {
      // ✅ 서버 경로에 맞게 수정
      // const res = await api.get(`/manager/systemNotification/list`, { params: { channel: currentChannel } });

      // 서버가 POST만 받는 구조면:
      // const res = await api.post('/manager/systemNotification/list', { channel: currentChannel });

      const res = await api.post('/manager/notification/list', {
        channel: currentChannel,
      });

      // 응답 구조 유연 처리 (auditlog.jsx처럼)
      const payload = res.data.data || res.data.resultData || res.data || {};
      console.log('시스템 알림 조회 응답 페이로드:', payload);
      const rows = payload.data || payload.items || payload || [];

      setItems(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('시스템 알림 조회 실패:', err);
      setErrorMsg('시스템 알림 메시지를 불러오는 중 오류가 발생했습니다.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [channel]);

  useEffect(() => {
    fetchList(channel);
  }, [channel]);

  const openEdit = (row) => {
    setEditing(row);
    setEditTitle(row.title || '');
    setEditContent(row.content || '');
    setIsOpen(true);
  };

  const closeEdit = () => {
    setIsOpen(false);
    setEditing(null);
    setEditTitle('');
    setEditContent('');
  };

  const isDirty = () => {
    if (!editing) return false;
    return (editTitle !== (editing.title || '')) || (editContent !== (editing.content || ''));
  };

  const handleSave = async () => {
    if (!editing) return;

    const title = String(editTitle || '').trim();
    const content = String(editContent || '').trim();

    if (!title) return alert('제목을 입력해주세요.');
    if (!content) return alert('내용을 입력해주세요.');

    setSaving(true);
    try {
      // ✅ 서버 경로에 맞게 수정
      // 1) PUT 방식
      // await api.put('/manager/systemNotification/update', { idx: editing.idx, title, content });

      // 2) POST 방식 (서버가 POST만 받으면)
      // await api.post('/manager/systemNotification/update', { idx: editing.idx, title, content });

      await api.put('/manager/notification/update', {
        idx: editing.idx,
        title,
        content,
      });

      // 저장 후 목록 재조회(갱신)
      await fetchList(channel);
      closeEdit();
      alert('저장되었습니다.');
    } catch (err) {
      console.error('시스템 알림 저장 실패:', err);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Menu>
      <div className="sn-page">
        <h2>시스템 알림 메시지 설정</h2>

        {/* 채널 탭 */}
        <div className="sn-tabs">
          {CHANNELS.map((ch) => (
            <button
              key={ch}
              type="button"
              className={`sn-tab ${channel === ch ? 'active' : ''}`}
              onClick={() => setChannel(ch)}
            >
              {ch}
            </button>
          ))}
        </div>

        {/* 상태 */}
        {loading && <div style={{ marginTop: 8 }}>로딩 중...</div>}
        {errorMsg && <div className="sn-error">{errorMsg}</div>}

        {/* 테이블 */}
        <table className="sn-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>채널</th>
              <th>제목</th>
              <th>내용</th>
              <th>업데이트</th>
              <th>편집</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 16 }}>
                  데이터가 없습니다.
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row.idx}>
                  <td>{row.idx}</td>
                  <td>{row.channel}</td>
                  <td>{row.title}</td>
                  <td className="sn-content-cell" title={row.content}>
                    {String(row.content || '').length > 80
                      ? `${String(row.content || '').slice(0, 80)}...`
                      : row.content}
                  </td>
                  <td>{formatDateTime(row.updated_at)}</td>
                  <td>
                    <button type="button" onClick={() => openEdit(row)}>
                      편집
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* 편집 모달 */}
        {isOpen && (
          <div className="sn-modal-backdrop" onMouseDown={closeEdit}>
            <div
              className="sn-modal"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="sn-modal-header">
                <div>
                  <div className="sn-modal-title">알림 메시지 편집</div>
                  <div className="sn-modal-sub">
                    ID: {editing?.idx} / 채널: {editing?.channel}
                  </div>
                </div>
                <button type="button" className="sn-close" onClick={closeEdit}>
                  ✕
                </button>
              </div>

              <div className="sn-modal-body">
                <label className="sn-label">
                  제목
                  <input
                    className="sn-input"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="알림 제목"
                    maxLength={50}
                  />
                </label>

                <label className="sn-label">
                  내용
                  <textarea
                    className="sn-textarea"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    placeholder="알림 내용"
                    rows={8}
                  />
                </label>

                <div className="sn-hint">
                  * 저장하면 앱/웹에서 다음 조회 시 변경된 문구가 적용됩니다.
                </div>
              </div>

              <div className="sn-modal-footer">
                <button type="button" onClick={closeEdit} disabled={saving}>
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!isDirty() || saving}
                  className="sn-primary"
                >
                  {saving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Menu>
  );
};

export default ManagerSystemNotification;
